import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Upload,
  Card,
  Alert,
  message,
  Select,
  Typography,
  Image,
} from "antd";
import {
  InboxOutlined,
  IdcardOutlined,
  CheckCircleOutlined,
  CarOutlined,
  CreditCardOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { mixGetV1, mixPutV1 } from "services/mixin";

const { Dragger } = Upload;
const { Title, Text } = Typography;
const { Option } = Select;

interface StepVerificationProps {
  setCurrentStep: (step: number) => void;
  profileData: any;
  setCompletedSteps: (steps: number[]) => void;
  completedSteps: number[];
  frontPreviewUrl: string
  setFrontPreviewUrl: (data: string) => void
  backPreviewUrl: string
  setBackPreviewUrl: (data: string) => void
  firstName: string
  lastName: string
  birthday: string | string[] | undefined
}

const documentTypes = [
  {
    key: "driving_license",
    icon: <CarOutlined className="!text-blue-500" />,
    title: "Driving License",
    subtitle: "Front and Back",
  },
  {
    key: "id_card",
    icon: <IdcardOutlined className="!text-green-500" />,
    title: "Passport",
    subtitle: "Signature Page",
  },
  {
    key: "citizen_card",
    icon: <CreditCardOutlined className="!text-purple-500" />,
    title: "National ID Card",
    subtitle: "Front and Back",
  },
];

const StepVerification: React.FC<StepVerificationProps> = ({
  setCurrentStep,
  profileData,
  setCompletedSteps,
  completedSteps,
  frontPreviewUrl,
  setFrontPreviewUrl,
  backPreviewUrl,
  setBackPreviewUrl,
  firstName,
  lastName,
  birthday
}) => {
  const [selectedCountry, setSelectedCountry] = useState("vietnam");
  const [selectedDocType, setSelectedDocType] = useState<string>("citizen_card");
  const [frontCardFile, setFrontCardFile] = useState<File | null>(null);
  const [backCardFile, setBackCardFile] = useState<File | null>(null);
  const [frontCardUploaded, setFrontCardUploaded] = useState(false);
  const [backCardUploaded, setBackCardUploaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createPreviewUrl = (file: File) => {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: "image/*",
    beforeUpload: (file: File) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("Please upload an image file");
        return false;
      }
      const isLt20M = file.size / 1024 / 1024 < 20;
      if (!isLt20M) {
        message.error("File size must be smaller than 20MB");
        return false;
      }
      return false;
    },
    showUploadList: false,
  };

  const handleDocumentTypeSelect = (docType: string) => {
    setSelectedDocType(docType);
    setFrontCardUploaded(false);
    setBackCardUploaded(false);
    setFrontCardFile(null);
    setBackCardFile(null);
    setFrontPreviewUrl("");
    setBackPreviewUrl("");
  };

  const handleFrontCardUpload = async (info: any) => {
    const file = info.file;
    setFrontCardFile(file);
    setFrontCardUploaded(true);
    const previewUrl = await createPreviewUrl(file);
    setFrontPreviewUrl(previewUrl);
    message.success("Front side uploaded successfully");
  };

  const handleBackCardUpload = async (info: any) => {
    const file = info.file;
    setBackCardFile(file);
    setBackCardUploaded(true);
    const previewUrl = await createPreviewUrl(file);
    setBackPreviewUrl(previewUrl);
    message.success("Back side uploaded successfully");
  };

  const handleReUploadFront = () => {
    setFrontCardUploaded(false);
    setFrontCardFile(null);
    setFrontPreviewUrl("");
  };

  const handleReUploadBack = () => {
    setBackCardUploaded(false);
    setBackCardFile(null);
    setBackPreviewUrl("");
  };

  const uploadData = async () => {
    function convertDate(input: string): string {
      const [day, month, year] = input.split("/");
      return `${year}-${month}-${day}`;
    }

    const dateHandle = birthday ? convertDate(String(birthday)) : ''

    function removeEmpty(obj: any) {
      return Object.fromEntries(
        Object.entries(obj).filter(([_, v]) => v !== undefined && v !== null && v !== "")
      );
    }

    const getData = localStorage.getItem('user')

    if (getData) {
      const idUser = JSON.parse(getData).id
      const payload = removeEmpty({
        first_name: firstName,
        last_name: lastName,
        birthday: dateHandle ? String(dateHandle) : undefined,
        is_active: true,
        cccd_front_image: frontPreviewUrl,
        cccd_back_image: backPreviewUrl
      })

      const res = await mixPutV1(`/api/v1/be/user/${idUser}`, payload)

      if (res.status) {
        const res2 = await mixGetV1(`/api/v1/be/user/${idUser}`)

        toast.success('Updated account information successfully', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });

        if (res2.status) {
          localStorage.setItem('user', JSON.stringify(res.data))
          setCurrentStep(2);
        }
      } else {
        toast.error('Updated account information failed', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
    }
  }

  const canProceed = selectedDocType === "id_card" ? frontCardUploaded : frontCardUploaded && backCardUploaded;

  return (
    <div className="identity-verification-step w-full min-w-[240px] max-w-[680px] mx-auto px-0 sm:px-6">
      <div className="text-start !mb-4 sm:!mb-6">
        <Title level={3} className="!text-white !text-xl sm:!text-2xl lg:!text-3xl">
          Identity Verification
        </Title>
      </div>

      {/* Country & Document Type Selection */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:space-x-4 space-y-4 sm:space-y-0">
        <div className="!mb-4 w-full">
          <Text className="text-white block mb-2 !font-medium text-sm sm:text-base">
            Issuing Country
          </Text>
          <Select
            value={selectedCountry}
            onChange={setSelectedCountry}
            className="w-full"
            size="large"
          >
            <Option className="hover:!bg-gray-300" value="vietnam">
              Vietnam
            </Option>
            <Option className="hover:!bg-gray-300" value="thailand">
              Thailand
            </Option>
            <Option className="hover:!bg-gray-300" value="singapore">
              Singapore
            </Option>
          </Select>
        </div>

        <div className="!mb-4 w-full">
          <Text className="text-white block mb-2 !font-medium text-sm sm:text-base">
            Document Type
          </Text>
          <Select
            value={selectedDocType}
            onChange={handleDocumentTypeSelect}
            className="w-full"
            size="large"
          >
            {documentTypes.map((doc) => (
              <Option key={doc.key} value={doc.key}>
                <div className="!flex !items-center space-x-2">
                  <span className="flex-shrink-0">{doc.icon}</span>
                  <div className="flex items-center space-x-1">
                    <div className="font-medium text-sm sm:text-base">{doc.title}</div>
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </div>
      </div>

      {/* Requirements Alert */}
      <Alert
        message="Requirements for ID Card/Passport/Driver's License"
        description={
          <ul className="list-disc list-inside text-xs sm:text-sm space-y-1 mt-2">
            <li>Must be a valid, non-expired photo ID</li>
            <li>Must contain your full legal name, photo, and date of birth</li>
            <li>
              Please provide copies of both the front and back of the ID, except for passports (Only the page with the signature)
            </li>
            <li>File size must not exceed 20MB</li>
          </ul>
        }
        type="info"
        showIcon
        className="!mb-4 sm:!mb-6 !bg-yellow-100 !border-yellow-300 !p-2 sm:!p-3"
      />

      {/* Upload Section */}
      <div className="upload-section" style={{ minHeight: "200px" }}>
        <div className={`flex ${selectedDocType === "id_card" ? 'justify-center' : 'flex-col lg:flex-row'} items-start gap-4`}>
          {/* Front Upload */}
          <div className="flex-1 w-full max-w-full lg:max-w-none">
            <Text className="text-white block mb-2 font-medium text-sm sm:text-base">
              {selectedDocType === "id_card" ? "Passport" : "Front Side"}
            </Text>
            <div className="relative">
              <Card className={`border-dashed !bg-transparent !border-gray-300 overflow-hidden w-full ${selectedDocType === "id_card" ? 'max-w-[320px] mx-auto lg:max-w-[280px]' : 'sm:max-w-[280px]'} h-[160px] sm:h-[180px] flex items-center justify-center p-0`}>
                {frontCardUploaded && frontPreviewUrl ? (
                  <div className="relative w-full h-full">
                    <Image
                      src={frontPreviewUrl}
                      alt="Front Document Preview"
                      width="100%"
                      height="100%"
                      style={{
                        objectFit: "cover",
                        maxWidth: "100%",
                        maxHeight: "180px",
                      }}
                      preview={{
                        mask: (
                          <div className="text-center">
                            <CheckCircleOutlined className="text-green-500 text-lg mb-1" />
                            <div className="text-white text-xs sm:text-sm">
                              Click to Enlarge
                            </div>
                          </div>
                        ),
                      }}
                    />
                  </div>
                ) : (
                  <Dragger
                    {...uploadProps}
                    onChange={handleFrontCardUpload}
                    className="border-none !bg-transparent hover:!bg-gray-100/10 !w-full !h-full flex items-center justify-center"
                    style={{ height: "100%" }}
                  >
                    <div className="text-center px-2">
                      <InboxOutlined className="!text-2xl sm:!text-4xl text-gray-400 mb-2" />
                      <div className="text-sm sm:text-lg font-medium text-gray-200 mb-1">
                        Upload Document
                      </div>
                      <div className="text-xs text-gray-400">
                        Drag & Drop or Click to Select
                      </div>
                    </div>
                  </Dragger>
                )}
              </Card>
              {frontCardUploaded && (
                <Button
                  type="primary"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={handleReUploadFront}
                  className="absolute top-2 right-2 !bg-blue-500 hover:!bg-blue-600 !border-blue-500 !z-10 text-xs sm:text-sm"
                >
                  Reload
                </Button>
              )}
            </div>
          </div>

          {/* Back Upload (only for non-passport documents) */}
          {selectedDocType !== "id_card" && (
            <div className="flex-1 w-full max-w-full lg:max-w-none">
              <Text className="text-white block mb-2 font-medium text-sm sm:text-base">
                Back Side
              </Text>
              <div className="relative">
                <Card className="border-dashed !bg-transparent !border-gray-300 overflow-hidden w-full sm:max-w-[280px] h-[160px] sm:h-[180px] flex items-center justify-center p-0">
                  {backCardUploaded && backPreviewUrl ? (
                    <div className="relative w-full h-full">
                      <Image
                        src={backPreviewUrl}
                        alt="Back Document Preview"
                        width="100%"
                        height="100%"
                        style={{
                          objectFit: "cover",
                          maxWidth: "100%",
                          maxHeight: "180px",
                        }}
                        preview={{
                          mask: (
                            <div className="text-center">
                              <CheckCircleOutlined className="text-green-500 text-lg mb-1" />
                              <div className="text-white text-xs sm:text-sm">
                                Click to Enlarge
                              </div>
                            </div>
                          ),
                        }}
                      />
                    </div>
                  ) : (
                    <Dragger
                      {...uploadProps}
                      onChange={handleBackCardUpload}
                      className="border-none !bg-transparent hover:!bg-gray-100/10 !w-full !h-full flex items-center justify-center"
                      style={{ height: "100%" }}
                    >
                      <div className="text-center px-2">
                        <InboxOutlined className="!text-2xl sm:!text-4xl text-gray-400 mb-2" />
                        <div className="text-sm sm:text-lg font-medium text-gray-200 mb-1">
                          Upload Document
                        </div>
                        <div className="text-xs text-gray-400">
                          Drag & Drop or Click to Select
                        </div>
                      </div>
                    </Dragger>
                  )}
                </Card>
                {backCardUploaded && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={handleReUploadBack}
                    className="absolute top-2 right-2 !bg-blue-500 hover:!bg-blue-600 !border-blue-500 !z-10 text-xs sm:text-sm"
                  >
                    Reload
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {!canProceed && (
          <Alert
            message="*If you currently do not have the necessary documents, you can come back later to upload them."
            type="warning"
            showIcon={false}
            className="text-center !bg-yellow-100 !border-yellow-300 mt-4 text-xs sm:text-sm"
          />
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-6">
        <Button
          type="default"
          size="large"
          onClick={uploadData}
          className="w-full sm:flex-1 h-10 sm:h-12 bg-transparent !border-[#E9B460]/30 text-white hover:!bg-[#E9B460]/[.8] hover:!border-[#E9B460]/50 text-sm sm:text-base"
        >
          Skip
        </Button>

        <Button
          type="primary"
          size="large"
          onClick={uploadData}
          disabled={!canProceed}
          loading={isSubmitting}
          className="w-full sm:flex-1 h-10 sm:h-12 text-white bg-yellow-500 hover:bg-yellow-600 border-yellow-500 hover:border-yellow-600 text-sm sm:text-base"
        >
          Send →
        </Button>
      </div>
    </div>
  );
};

export default StepVerification;
